import { hasPermission, Permissions } from './Permissions'

export interface ManagementLink {
  label: string
  path: string
  permission?: string
}

export function companyLinks(canManage: boolean): ManagementLink[] {
  return [
    { label: 'Company overview', path: '/company/' },
    { label: 'Teams', path: '/teams/' },
    { label: 'Users', path: '/users/' },
    { label: 'Equipment', path: '/company/equipment/' },
    { label: 'Brokers', path: '/brokers/' },
    ...(canManage
      ? [
          { label: 'Agents', path: '/agents/' },
          { label: 'Driver onboarding', path: '/company/drivers/' },
          { label: 'Driver performance', path: '/company/performance/' },
          { label: 'Safety', path: '/company/safety/' },
          { label: 'Advanced settings', path: '/company/advanced-settings/' },
          { label: 'Company finance', path: '/company/finance/' },
        ]
      : []),
  ]
}

export function companyLinkActive(pathname: string, path: string) {
  const current = pathname.replace(/\/$/, '')
  const target = path.replace(/\/$/, '')
  if (target === '/company') return current === target || current === '/company/setup'
  if (target === '/company/equipment') return /^\/(trucks|trailers)\//.test(current) || current === target
  return current === target || current.startsWith(`${target}/`)
}

export function managementLinks(user: {
  isStaff?: boolean
  isOrgAdmin?: boolean
  permissions?: any
  isSuperUser?: boolean
  featureFlags?: any
}): ManagementLink[] {
  if (!user.isStaff) return companyLinks(!!user.isOrgAdmin)
  return [
    { label: 'Organizations', path: '/organizations' },
    { label: 'Issues', path: '/issues' },
    { label: 'Inbox', path: '/inbox' },
    { label: 'Load recovery', path: '/load-recovery-tool' },
    { label: 'Users', path: '/users', permission: Permissions.ViewCustomUser },
    { label: 'Teams', path: '/teams', permission: Permissions.ViewTeam },
    { label: 'Jobs', path: '/jobs', permission: Permissions.ViewJob },
    { label: 'Statements', path: '/statements', permission: Permissions.ViewStatement },
    { label: 'Invoices', path: '/invoices', permission: Permissions.ViewInvoice },
    { label: 'Outbound loads', path: '/load-syncs', permission: Permissions.ViewLoadSync },
    { label: 'Loads', path: '/loads', permission: Permissions.ViewLoad },
    { label: 'Contacted loads', path: '/contacted-loads', permission: Permissions.ViewContactedLoad },
    { label: 'Custom loads', path: '/custom-loads', permission: Permissions.ViewCustomLoad },
    { label: 'Schedules', path: '/schedules', permission: Permissions.ViewSchedule },
    { label: 'Load search', path: '/searches', permission: Permissions.ViewLoad },
    { label: 'Search zones', path: '/search-zones', permission: Permissions.ViewSearchZone },
    { label: 'Brokers', path: '/brokers', permission: Permissions.ViewBroker },
    ...(/localhost|staging/.test(window.location.hostname)
      ? [{ label: 'QA scenarios', path: '/qa', permission: Permissions.ViewQA }]
      : []),
    { label: 'Fuel receipts', path: '/fuel-receipts', permission: Permissions.ViewFuelReceipt },
    { label: 'Trucks', path: '/trucks' },
    ...(hasPermission(user, Permissions.ViewEquipment, 'public-equipment-management')
      ? [{ label: 'Trailers', path: '/trailers' }]
      : []),
    { label: 'Job tracking', path: '/job-tracking', permission: Permissions.ViewJob },
    { label: 'Permission groups', path: '/permission-groups', permission: Permissions.ViewPermissionGroup },
    {
      label: 'Safety & performance',
      path: '/compliance-safety-tool',
      permission: Permissions.ViewSafetyTool,
    },
    { label: 'Driver feasibility', path: '/feasibility-stat', permission: Permissions.ViewFeasibilityStat },
    { label: 'Dispatch', path: '/dispatcher-home' },
  ].filter((item) => !item.permission || hasPermission(user, item.permission))
}
